import { useState, useEffect, useRef, useMemo } from "react";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

const SearchableDropdownOptions = ({
  options = [],
  value,
  onChange,
  placeholder = "Search...",
  isLoading = false,
  onSearch,
  disabled = false,
  fullWidth = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const dropdownRef = useRef(null);

  const serializedOptions = JSON.stringify(options || []);
  const memoizedOptions = useMemo(() => {
    if (!options || !Array.isArray(options)) {
      return [];
    }
    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedOptions]);

  useEffect(() => {
    if (!searchInput) {
      setFilteredOptions(memoizedOptions);
    } else {
      setFilteredOptions(
        memoizedOptions.filter((option) =>
          String(option.label)
            .toLowerCase()
            .includes(searchInput.toLowerCase())
        )
      );
    }
  }, [memoizedOptions, searchInput]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setSearchInput(inputValue);

    if (onSearch) {
      onSearch(inputValue);
    } else {
      const filtered = memoizedOptions.filter((option) =>
        String(option.label).toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  };

  const handleSelect = (selectedValue) => {
    if (onChange) {
      onChange(selectedValue);
    }
    setIsOpen(false);
    setSearchInput("");
    setFilteredOptions(memoizedOptions);
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (onChange) {
      onChange(null);
    }
    setSearchInput("");
    setFilteredOptions(memoizedOptions);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchInput("");
        setFilteredOptions(memoizedOptions);
      }
    }
  };

  return (
    <div
      className={`relative ${fullWidth ? "w-full" : "w-full md:w-[200px]"}`}
      ref={dropdownRef}
    >
      <div className="relative">
        <div
          className={`flex items-center justify-between w-full rounded-lg border py-1.5 pl-3 pr-10 text-gray-900 ${
            disabled
              ? "border-gray-200 bg-gray-50"
              : "border-gray-300 bg-white hover:border-gray-400"
          } focus:ring-2 focus:ring-blue-500/20 sm:text-sm sm:leading-5 cursor-pointer transition`}
          onClick={toggleDropdown}
          style={{
            cursor: disabled === true ? "not-allowed" : "pointer",
          }}
        >
          {value !== undefined && value !== null && value !== "" ? (
            <span className="truncate">
              {memoizedOptions.find((opt) => opt.value === value)?.label ||
                String(value)}
            </span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}

          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {value !== undefined &&
              value !== null &&
              value !== "" &&
              !disabled && (
                <button
                  type="button"
                  className="p-1 hover:bg-gray-100 rounded z-10"
                  onClick={clearSelection}
                  tabIndex={-1}
                >
                  <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            <ChevronDownIcon
              className={`h-4 w-4 text-gray-400 transition-transform ${
                isOpen ? "transform rotate-180" : ""
              }`}
              aria-hidden="true"
            />
          </div>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full rounded-xl bg-white shadow-xl border border-gray-200 focus:outline-none">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <input
                type="text"
                className="block w-full rounded-lg border border-gray-300 py-1.5 pl-3 pr-9 text-gray-900 focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20 sm:text-sm"
                placeholder="Search..."
                value={searchInput}
                onChange={handleInputChange}
                autoFocus
              />
              <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                {isLoading ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="animate-spin h-4 w-4 text-blue-600"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    fill="none"
                  >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                ) : (
                  <MagnifyingGlassIcon
                    className="h-4 w-4 text-gray-400"
                    aria-hidden="true"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="max-h-60 overflow-auto py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={String(option.value)}
                  className={`px-3.5 py-2 text-xs font-medium cursor-pointer transition ${
                    value === option.value
                      ? "bg-blue-50 text-blue-700 font-semibold"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className="px-3.5 py-2 text-xs text-gray-400">
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdownOptions;
